import { FC } from "react";
import { useRecoilValue } from "recoil";
import { useBaseTable } from "../../../../hooks/useBaseTable";
import { currencyFormat } from "../../../../utilities/currencyFunctions";
import { BaseTable2, Col, FontBold } from "../../../TrueUI";
import Row, { rowWithNoMarginNorGutter } from "../../../TrueUI/Grids/Row";
import { BaseTable2Properties } from "../../../TrueUI/Tables/BaseTable2/TableProperties";
import MultiTableChildrenComponent from "./MultiTableChildrenComponent";
import MultiTableParentComponent from "./MultiTableParentComponent";
import { BindInstructionsInfoAtom } from "./PolicyQuoteBindInstructionsAtoms";
import { PolicyQuoteBindInstructionsTableProps } from "./PolicyQuoteBindInstructionsTypes";
import {
  getChildrenData,
  getCommonTablesProps,
  getDueDateByHeaderParam,
} from "./PolicyQuoteBindInstructionsUtils";

const PolicyQuoteBindInstructionsMultiTable: FC<
  PolicyQuoteBindInstructionsTableProps
> = ({ insuredId, readOnly, setIsDialogOpen }) => {
  const TABLE_NAME = `policy_payment_schedule_multi_table_${insuredId}`;
  const bindInstructionsInfo = useRecoilValue(BindInstructionsInfoAtom);

  const scheduleTableInfo = bindInstructionsInfo?.scheduleTableInfo;

  const CustomMultiTableFooterComponent = (groups: any[]) => {
    if ((groups?.length ?? 0) <= 0) {
      return <></>;
    }
    const totalPremium =
      (bindInstructionsInfo?.billedDepositAmount ?? 0) +
      (bindInstructionsInfo?.installmentPayment ?? 0) *
        ((bindInstructionsInfo?.payPlan?.numberOfPayment ?? 0) - 1);
    const total = totalPremium + (bindInstructionsInfo?.billedFees ?? 0);
    return (
      <Row {...rowWithNoMarginNorGutter} numberOfColumns={104} rowHeight="41px">
        <Col breakpoints={{ md: 16, lg: 16, xl: 16 }} />
        <Col
          breakpoints={{ md: 25, lg: 25, xl: 25 }}
          horizontalAlign="flex-start"
        >
          <FontBold>Total of all payments</FontBold>
        </Col>
        <Col
          horizontalMargin="0px"
          horizontalGutter="0px"
          breakpoints={{ md: 20, lg: 20, xl: 20 }}
          horizontalAlign="flex-end"
          verticalAlign="center"
        >
          <FontBold>
            {currencyFormat(bindInstructionsInfo?.billedFees, false, 0)}
          </FontBold>
        </Col>
        <Col breakpoints={{ md: 1, lg: 1, xl: 1 }} />
        <Col
          horizontalMargin="0px"
          horizontalGutter="0px"
          breakpoints={{ md: 19, lg: 19, xl: 19 }}
          horizontalAlign="flex-end"
          verticalAlign="center"
        >
          <FontBold>{currencyFormat(totalPremium, false, 0)}</FontBold>
        </Col>
        <Col breakpoints={{ md: 1, lg: 1, xl: 1 }} />
        <Col
          horizontalMargin="0px"
          horizontalGutter="0px"
          breakpoints={{ md: 20, lg: 20, xl: 20 }}
          horizontalAlign="flex-end"
          verticalAlign="center"
        >
          <FontBold>{currencyFormat(total, false, 0)}</FontBold>
        </Col>
        <Col breakpoints={{ md: 2, lg: 2, xl: 2 }} />
      </Row>
    );
  };

  const tableConfiguration: BaseTable2Properties = {
    name: TABLE_NAME,
    tableType: "multi",
    advancedOptions: {
      headerExportType: "aligned",
      tableStyle: { height: "fit-content" },
      paginate: false,
      disableOrderBy: true,
      multiTableProperties: {
        MultiTablePatternComponent: (e) => (
          <MultiTableChildrenComponent
            detailData={e}
            scheduleTable={scheduleTableInfo}
          />
        ),
        MultiTableHeaderPatternComponent: (params, obj) => (
          <MultiTableParentComponent
            readOnly={readOnly ?? true}
            metaData={obj}
            paramDate={getDueDateByHeaderParam(params)}
            headerParams={params ?? []}
            columnOptions={[
              {
                fieldName: "DueDate",
                width: 22,
              },
              {
                fieldName: "Description",
                width: 38,
              },
              {
                fieldName: "NonEarning",
                align: "right",
                width: 0,
              },
              {
                fieldName: "Fees",
                align: "right",
                width: 30,
              },
              {
                fieldName: "Premium",
                align: "right",
                width: 30,
              },
              {
                fieldName: "Total",
                align: "right",
                width: 30,
              },
            ]}
            setIsDialogOpen={setIsDialogOpen}
          />
        ),
      },
    },
    childrenData: getChildrenData(scheduleTableInfo),
    parentMetaData: scheduleTableInfo?.scheduleMultiTable?.parentMetaData,
    columnsAndGroups: {
      columns: scheduleTableInfo?.scheduleMultiTable?.columns ?? [],
      groups: scheduleTableInfo?.scheduleMultiTable?.groups ?? [],
    },
    footerOptions: {
      CustomFooterComponent: () =>
        CustomMultiTableFooterComponent(
          scheduleTableInfo?.scheduleMultiTable?.groups ?? []
        ),
    },
    ...getCommonTablesProps(readOnly),
  };

  useBaseTable(tableConfiguration);

  return <BaseTable2 name={TABLE_NAME} />;
};

export default PolicyQuoteBindInstructionsMultiTable;
